import * as React from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { ImeasurementPoint, IMaintenanceTrigger } from '../../models';
import { isManualEntryDateRequired, resolveMaintenanceFieldValues } from '../../actions/maintenanceTriggerUtil';
import { stageMaintenanceTrigger } from '../../actions/maintenanceTriggerActions';
import { getPendingMaintenanceTrigger, getSavedMaintenanceTrigger } from '../../reducers/maintenanceTriggerReducer';
import { IinitialState } from '../../reducers';
import { constants } from '../../constants/constants';
import DatetimePortal from '../common/DatetimePortal';
import { RequiredIndicator } from '../common/FormUtil';

interface Iprops {
    mp: ImeasurementPoint;
    installBaseID: string;
    /*
     * Read only while a historical row is open: what is on screen was recorded on a closed job, and
     * an edit here would stage a maintenance trigger against the job that is actually open
     * (MMG-4345).
     */
    disabled?: boolean;
}

// mp.maintenancePeriod -> amount/unit to add to the last-maintained date to get the next due date.
// Mirrors constants.maintenancePeriodOptions.
const maintenancePeriodAdd: { [value: number]: [number, moment.unitOfTime.DurationConstructor] } = {
    1: [3, 'months'],
    2: [6, 'months'],
    3: [12, 'months'],
    4: [2, 'years'],
    5: [3, 'years'],
    6: [5, 'years'],
    7: [54, 'months']
};

const MaintenanceMPControl: React.FC<Iprops> = ({ mp, installBaseID, disabled = false }) => {
    const { t } = useTranslation(['manageMeasurementPointLists', 'common']);
    const dispatch = useDispatch();

    const maintenanceType = mp.maintenanceType ?? 1;

    // Editable values come from the MP answer (the job's source of truth), with in-session pending edits
    // taking precedence and the install-base trigger as the fallback. Triggers are pre-synced at job open
    // (getMaintenanceTriggersForFacility), so this renders/falls back offline with no per-asset fetch.
    const pending = useSelector((state: IinitialState) =>
        getPendingMaintenanceTrigger(state, installBaseID, maintenanceType)
    );
    const syncedTrigger = useSelector((state: IinitialState) =>
        getSavedMaintenanceTrigger(state, installBaseID, maintenanceType)
    );
    const answer = useSelector((state: IinitialState) =>
        (state.measurementPointResults.selectedResult.measurementPointAnswers || []).find(
            a => a.measurementPointID === mp.id
        )
    );

    const values = resolveMaintenanceFieldValues(pending, answer, syncedTrigger);

    // Stage edits as a pending trigger; Save & Log projects these into the MP answer (see
    // submitMeasurementPointResult). Seed from the currently-displayed values so each edit merges cleanly.
    const handleChange = (partial: Partial<IMaintenanceTrigger>) => {
        // "Unknown" is mutually exclusive with completion: checking it clears Was Maintenance Completed
        // and Date Last Maintained. It also clears Snooze, since the server gives Unknown precedence over
        // snooze at job close - keeping the UI in sync with the persisted outcome. Completing or
        // back-dating clears Unknown.
        let adjusted: Partial<IMaintenanceTrigger> = partial;
        if (partial.isUnknown === true) {
            adjusted = { ...partial, maintenanceCompleted: false, manualEntryDate: '', isSnoozed: false };
        } else if (partial.maintenanceCompleted === true || !!partial.manualEntryDate) {
            adjusted = { ...partial, isUnknown: false };
        }
        const base: IMaintenanceTrigger = pending ?? {
            id: uuidv4(),
            isDeleted: false,
            installBaseID,
            maintenanceType,
            maintenancePeriod: mp.maintenancePeriod ?? 1,
            maintenanceDuePrior: mp.maintenanceDuePrior ?? 1,
            isEnabled: values.isEnabled,
            maintenanceCompleted: values.maintenanceCompleted,
            isSnoozed: values.isSnoozed,
            isUnknown: values.isUnknown,
            measurementPointID: mp.id,
            ...(values.manualEntryDate ? { manualEntryDate: values.manualEntryDate } : {})
        };
        dispatch(stageMaintenanceTrigger({ ...base, ...adjusted, measurementPointID: mp.id }));
    };

    const typeLabel = constants.maintenanceTypeOptions.find(o => o.value === mp.maintenanceType)?.label ?? '';
    const periodLabel = constants.maintenancePeriodOptions.find(o => o.value === mp.maintenancePeriod)?.label ?? '';

    const manualEntryMoment = values.manualEntryDate ? moment(values.manualEntryDate) : undefined;

    const manualEntryRequired = isManualEntryDateRequired({
        maintenanceType,
        pending,
        answer,
        synced: syncedTrigger
    });

    // Next due = last maintained date + maintenance period. Prefer the in-session "Date Last Maintained"
    // so the value updates live as it's edited, falling back to the synced last-completed date. When
    // neither is available (e.g. a trigger flagged Unknown - the server back-dates NextDueDate without a
    // LastCompletedDate), fall back to the server's persisted NextDueDate so the overdue date still shows.
    const lastMaintainedMoment =
        manualEntryMoment ?? (syncedTrigger?.lastCompletedDate ? moment(syncedTrigger.lastCompletedDate) : undefined);
    const periodAdd = maintenancePeriodAdd[mp.maintenancePeriod ?? 0];
    const nextDueMoment =
        lastMaintainedMoment && periodAdd
            ? moment(lastMaintainedMoment).add(periodAdd[0], periodAdd[1])
            : syncedTrigger?.nextDueDate
            ? moment(syncedTrigger.nextDueDate)
            : undefined;
    const nextDueLabel = nextDueMoment ? nextDueMoment.format('MM/DD/YYYY') : '';

    return (
        <Col xs={12} className="mp-row maintenance-mp-control">
            <Row>
                {/* Maintenance points have no question text; the type is the title */}
                <Col xs={12}>
                    <h5 className="mp-header">{typeLabel}</h5>
                </Col>

                {/* Maintenance config display (read-only, from MP definition) */}
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Label>{t('maintenancePeriod')}</Form.Label>
                        <Form.Control plaintext readOnly value={periodLabel} />
                    </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Label>{t('maintenanceNextDue')}</Form.Label>
                        <Form.Control plaintext readOnly value={nextDueLabel} />
                    </Form.Group>
                </Col>

                {/* Enable toggle */}
                <Col xs={12}>
                    <Form.Group>
                        <Form.Check
                            type="switch"
                            id={`trigger-enabled-${mp.id}`}
                            label={t('triggerEnabled')}
                            disabled={disabled}
                            checked={values.isEnabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange({ isEnabled: e.target.checked })
                            }
                        />
                    </Form.Group>
                </Col>

                {/* Fields gated behind isEnabled */}
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Label>{t('wasMaintenanceCompleted')}</Form.Label>
                        <Form.Control
                            as="select"
                            aria-label={t('wasMaintenanceCompleted')}
                            disabled={disabled || !values.isEnabled || values.isUnknown}
                            value={values.maintenanceCompleted ? '1' : '0'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handleChange({ maintenanceCompleted: e.target.value === '1' })
                            }
                        >
                            <option value="0">{t('common:no')}</option>
                            <option value="1">{t('common:yes')}</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                {/* Manual entry date */}
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Label>
                            {t('manualEntryDate')}
                            <RequiredIndicator meta={{ required: manualEntryRequired }} optionalText="" />
                        </Form.Label>
                        <DatetimePortal
                            {...(manualEntryMoment ? { value: manualEntryMoment } : {})}
                            timeFormat={false}
                            dateFormat="MM/DD/YYYY"
                            inputProps={{
                                disabled:
                                    disabled || !values.isEnabled || values.maintenanceCompleted || values.isUnknown,
                                placeholder: 'MM/DD/YYYY'
                            }}
                            onChange={(val: any) => {
                                if (moment.isMoment(val)) {
                                    handleChange({ manualEntryDate: val.toISOString() });
                                } else if (!val) {
                                    // Cleared the field - stage the clear so it isn't carried forward.
                                    handleChange({ manualEntryDate: '' });
                                }
                            }}
                        />
                    </Form.Group>
                </Col>

                {/* Snooze 12 months — single yes/no action; SnoozeUntilDate is server-stamped at CompleteJob */}
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Label>{t('snoozeTwelveMonths')}</Form.Label>
                        <Form.Control
                            as="select"
                            aria-label={t('snoozeTwelveMonths')}
                            disabled={disabled || !values.isEnabled || values.isUnknown}
                            value={values.isSnoozed ? '1' : '0'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handleChange({ isSnoozed: e.target.value === '1' })
                            }
                        >
                            <option value="0">{t('common:no')}</option>
                            <option value="1">{t('common:yes')}</option>
                        </Form.Control>
                    </Form.Group>
                </Col>

                {/* Read-only status (server-computed at job close, from the synced trigger) */}
                {syncedTrigger?.lastCompletedDate && (
                    <Col xs={12} sm={6}>
                        <Form.Group>
                            <Form.Label>{t('lastMaintained')}</Form.Label>
                            <Form.Control
                                plaintext
                                readOnly
                                value={moment(syncedTrigger.lastCompletedDate).format('MM/DD/YYYY')}
                            />
                        </Form.Group>
                    </Col>
                )}

                {/* Unknown: tech can't service it and has no maintenance history. Mutually exclusive with
                    completion; the server back-dates NextDueDate at job close so it surfaces as Due. */}
                <Col xs={12} sm={6}>
                    <Form.Group>
                        <Form.Check
                            type="checkbox"
                            id={`trigger-unknown-${mp.id}`}
                            label={t('maintenanceUnknown')}
                            disabled={disabled || !values.isEnabled || values.maintenanceCompleted}
                            checked={values.isUnknown}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleChange({ isUnknown: e.target.checked })
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>
        </Col>
    );
};

export default MaintenanceMPControl;
